import React, {
    forwardRef,
    memo,
    useCallback,
    useImperativeHandle,
    useMemo,
    useRef,
    type CSSProperties,
} from 'react';
import {
    FixedSizeTree,
    type FixedSizeNodeData,
    type TreeWalker,
    type TreeWalkerValue,
} from 'react-vtree';
import type { Align } from 'react-window';
import {
    type FacilityAssetSetTreeIndex,
    type FacilityAssetSetTreeNode,
    type FacilityAssetSetTreeProjection,
    type FacilityAssetSetTreeRowId,
} from '../helpers/facilityAssetSetTreeModel';
import UrlUtilities from '@shared/common/UrlUtilities';

const ROW_HEIGHT = 36;
const ROW_BASE_PADDING_LEFT = 5;
const ROW_INDENT_STEP = 10;
const OVERSCAN_COUNT = 6;
const EMPTY_CHILDREN: FacilityAssetSetTreeRowId[] = [];

type RowData = FixedSizeNodeData & {
    node: FacilityAssetSetTreeNode;
    nestingLevel: number;
    hasChildren: boolean;
};

type WalkerMeta = { nestingLevel: number };

interface VirtualTreeData {
    selectedAssetSetId: string | null;
    onToggle(rowId: FacilityAssetSetTreeRowId, open: boolean): void;
    onActivate(node: FacilityAssetSetTreeNode): void;
    onMenu(event: React.MouseEvent<HTMLButtonElement>, node: FacilityAssetSetTreeNode): void;
    canShowMenu(node: FacilityAssetSetTreeNode): boolean;
    renderRowAccessory?(node: FacilityAssetSetTreeNode): React.ReactNode;
}

export interface FacilityAssetSetVirtualTreeApi {
    scrollToItem(rowId: FacilityAssetSetTreeRowId, align?: Align): void;
    setOpen(rowId: FacilityAssetSetTreeRowId, open: boolean): Promise<void>;
    recomputeExpanded(rowIds: Iterable<FacilityAssetSetTreeRowId>): Promise<void>;
}

export interface FacilityAssetSetVirtualTreeProps {
    index: FacilityAssetSetTreeIndex;
    projection: FacilityAssetSetTreeProjection;
    isSearchActive: boolean;
    expandedRowIdsRef: React.MutableRefObject<Set<FacilityAssetSetTreeRowId>>;
    filteredExpandedRowIdsRef: React.MutableRefObject<Set<FacilityAssetSetTreeRowId>>;
    selectedAssetSetId: string | null;
    onActivate(node: FacilityAssetSetTreeNode): void;
    onExpansionChanged(): void;
    onMenu(event: React.MouseEvent<HTMLButtonElement>, node: FacilityAssetSetTreeNode): void;
    canShowMenu(node: FacilityAssetSetTreeNode): boolean;
    emptyMessage: string;
    height: number;
    width: number | string;
    renderHeader?: () => React.ReactNode;
    renderRowAccessory?: (node: FacilityAssetSetTreeNode) => React.ReactNode;
}

export const FacilityAssetSetVirtualTree = forwardRef<FacilityAssetSetVirtualTreeApi, FacilityAssetSetVirtualTreeProps>(
    function FacilityAssetSetVirtualTree(props, apiRef) {
        const treeRef = useRef<FixedSizeTree<RowData>>(null);
        const propsRef = useRef(props);
        propsRef.current = props;

        const {
            index,
            projection,
            isSearchActive,
            expandedRowIdsRef,
            filteredExpandedRowIdsRef,
            selectedAssetSetId,
            onActivate,
            onExpansionChanged,
            onMenu,
            canShowMenu,
            emptyMessage,
            height,
            width,
            renderHeader,
            renderRowAccessory,
        } = props;

        const { treeWalker, visibleRootCount } = useMemo(() => {
            const included = projection.includedRowIds;
            const forcedOpen = projection.forcedOpenRowIds;
            const expanded = isSearchActive ? filteredExpandedRowIdsRef.current : expandedRowIdsRef.current;
            const childCache = new Map<FacilityAssetSetTreeRowId, FacilityAssetSetTreeRowId[]>();

            const visibleChildren = (rowId: FacilityAssetSetTreeRowId) => {
                const cached = childCache.get(rowId);
                if (cached) return cached;
                const children = index.childrenById.get(rowId) ?? EMPTY_CHILDREN;
                const visible = included === null ? children : children.filter(childId => included.has(childId));
                childCache.set(rowId, visible);
                return visible;
            };

            const toValue = (
                rowId: FacilityAssetSetTreeRowId,
                nestingLevel: number
            ): TreeWalkerValue<RowData, WalkerMeta> => ({
                data: {
                    id: rowId,
                    isOpenByDefault: expanded.has(rowId) || forcedOpen.has(rowId),
                    node: index.byId.get(rowId)!,
                    nestingLevel,
                    hasChildren: visibleChildren(rowId).length > 0,
                },
                nestingLevel,
            });

            const visibleRoots = included === null
                ? index.rootIds
                : index.rootIds.filter(rootId => included.has(rootId));
            const walker: TreeWalker<RowData, WalkerMeta> = function* () {
                for (const rootId of visibleRoots) {
                    yield toValue(rootId, 0);
                }
                while (true) {
                    const parent = yield;
                    for (const childId of visibleChildren(parent.data.id)) {
                        yield toValue(childId, parent.nestingLevel + 1);
                    }
                }
            };

            return { treeWalker: walker, visibleRootCount: visibleRoots.length };
            // Expansion refs intentionally do not rebuild the walker. Interactive
            // changes go through recomputeTree and structural rebuilds read the refs.
        }, [expandedRowIdsRef, filteredExpandedRowIdsRef, index, isSearchActive, projection]);

        const handleToggle = useCallback((rowId: FacilityAssetSetTreeRowId, open: boolean) => {
            const expanded = isSearchActive ? filteredExpandedRowIdsRef.current : expandedRowIdsRef.current;
            if (open) expanded.add(rowId);
            else expanded.delete(rowId);
            onExpansionChanged();
            void treeRef.current?.recomputeTree({ [rowId]: open });
        }, [expandedRowIdsRef, filteredExpandedRowIdsRef, isSearchActive, onExpansionChanged]);

        const handleActivate = useCallback((node: FacilityAssetSetTreeNode) => {
            if (node.itemType === 'root') {
                onActivate(node);
                return;
            }
            const record = treeRef.current?.state.records.get(node.id);
            if (record) handleToggle(node.id, !record.public.isOpen);
        }, [handleToggle, onActivate]);

        const treeData = useMemo<VirtualTreeData>(() => ({
            selectedAssetSetId,
            onToggle: handleToggle,
            onActivate: handleActivate,
            onMenu,
            canShowMenu,
            renderRowAccessory,
        }), [canShowMenu, handleActivate, handleToggle, onMenu, renderRowAccessory, selectedAssetSetId]);

        useImperativeHandle(apiRef, () => ({
            scrollToItem: (rowId, align) => treeRef.current?.scrollToItem(rowId, align),
            setOpen: async (rowId, open) => {
                const currentProps = propsRef.current;
                const expanded = currentProps.isSearchActive
                    ? currentProps.filteredExpandedRowIdsRef.current
                    : currentProps.expandedRowIdsRef.current;
                if (open) expanded.add(rowId);
                else expanded.delete(rowId);
                currentProps.onExpansionChanged();
                await treeRef.current?.recomputeTree({ [rowId]: open });
            },
            recomputeExpanded: async rowIds => {
                const currentIndex = propsRef.current.index;
                const ordered = [...rowIds].sort((left, right) => {
                    const depth = (rowId: FacilityAssetSetTreeRowId) => {
                        let value = 0;
                        let parentId = currentIndex.parentById.get(rowId) ?? null;
                        while (parentId) {
                            value += 1;
                            parentId = currentIndex.parentById.get(parentId) ?? null;
                        }
                        return value;
                    };
                    return depth(left) - depth(right);
                });
                await treeRef.current?.recomputeTree(Object.fromEntries(ordered.map(rowId => [rowId, true])));
            },
        }), []);

        if (visibleRootCount === 0) {
            return (
                <div className="facility-asset-sets-tree-empty" style={{ height, width }}>
                    {emptyMessage}
                </div>
            );
        }

        return (
            <div className="facility-asset-set-virtual-tree-with-header" style={{ height, width }}>
                {renderHeader?.()}
                <FixedSizeTree<RowData>
                    ref={treeRef}
                    className="facility-asset-sets-tree"
                    treeWalker={treeWalker}
                    itemSize={ROW_HEIGHT}
                    height={Math.max(0, height - (renderHeader ? ROW_HEIGHT : 0))}
                    width={width}
                    overscanCount={OVERSCAN_COUNT}
                    itemData={treeData}
                >
                    {VirtualTreeRow}
                </FixedSizeTree>
            </div>
        );
    }
);

interface VirtualTreeRowProps {
    data: RowData;
    isOpen: boolean;
    style: CSSProperties;
    treeData?: VirtualTreeData;
}

const getIconClass = (node: FacilityAssetSetTreeNode) => {
    if (node.itemType === 'tag') return 'tag-node';
    if (node.itemType !== 'root') return '';
    if (node.isSystem) return 'static-locked-asset-set';
    return node.type === AssetSetType.List ? 'fixed-asset-set' : 'smart-asset-set';
};

const getIconSource = (node: FacilityAssetSetTreeNode) => {
    if (node.itemType === 'knowledgeBook') {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/KB/BookBlue.png`;
    }
    if (node.itemType === 'schema') {
        return `${UrlUtilities.getBaseApplicationUrl()}/images/schema/Achema.png`;
    }
    return null;
};

const VirtualTreeRow = memo(function VirtualTreeRow({ data, isOpen, style, treeData: rawTreeData }: VirtualTreeRowProps) {
    const treeData = rawTreeData!;
    const { node, nestingLevel, hasChildren } = data;
    const isSelected = node.itemType === 'root' && node.assetSetId === treeData.selectedAssetSetId;
    const iconClass = getIconClass(node);
    const isRootTag = node.itemType === 'tag' && node.parentId?.startsWith('schema:');
    const className = [
        'facility-asset-set-virtual-row',
        `facility-asset-set-${node.itemType}-row`,
        isRootTag ? 'facility-asset-set-root-tag-row' : '',
        isSelected ? 'is-selected' : '',
        'cursor-pointer',
    ].filter(Boolean).join(' ');
    const iconSource = getIconSource(node);

    return (
        <div
            className={className}
            data-row-id={node.id}
            data-asset-set-id={node.assetSetId}
            style={{ ...style, paddingLeft: ROW_BASE_PADDING_LEFT + nestingLevel * ROW_INDENT_STEP }}
            onClick={() => treeData.onActivate(node)}
        >
            <button
                type="button"
                className={`no-btn facility-asset-set-expander${hasChildren ? '' : ' is-leaf'}`}
                onClick={event => {
                    event.stopPropagation();
                    if (hasChildren) treeData.onToggle(node.id, !isOpen);
                }}
            >
                {hasChildren && <span className={`e-icons expander-icon e-${isOpen ? 'down' : 'right'}`} />}
            </button>
            {iconSource ? (
                <img
                    alt=""
                    aria-hidden="true"
                    className="tree-item-icon"
                    src={iconSource}
                    onError={event => {
                        if (node.itemType !== 'schema' || event.currentTarget.dataset.fallback === 'true') {
                            return;
                        }
                        event.currentTarget.dataset.fallback = 'true';
                        event.currentTarget.src = `${UrlUtilities.getBaseApplicationUrl()}/images/schema/Schema.png`;
                    }}
                />
            ) : iconClass ? <span className={`tree-item-icon ${iconClass}`} /> : null}
            <span className="parent-tree-name-text display-name-text" title={node.name}>{node.name}</span>
            {treeData.renderRowAccessory?.(node)}
            <div className="tree-actions">
                {treeData.canShowMenu(node) && (
                    <button
                        type="button"
                        className="no-btn"
                        title={InovxLoader.t('facility:grid.commands')}
                        onClick={event => {
                            event.stopPropagation();
                            treeData.onMenu(event, node);
                        }}
                    >
                        <i className="fa fa-ellipsis-v" />
                    </button>
                )}
            </div>
        </div>
    );
});

export default FacilityAssetSetVirtualTree;
